import { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Module-level guard so even multiple simultaneous 401s show ONE toast and
// trigger ONE redirect. Reset on next successful login.
let sessionExpiredGuard = false;

const looksLikeAuthError = (status, detail) => {
  if (status !== 401 && status !== 403) return false;
  if (typeof detail !== 'string') return true; // any 401/403 counts
  const d = detail.toLowerCase();
  return (
    d.includes('token expired') ||
    d.includes('invalid token') ||
    d.includes('no authorization') ||
    d.includes('authentication required') ||
    d.includes('not authenticated') ||
    d.includes('signature')
  );
};

// Lightweight JWT exp parse — no library, no signature verify, only used to
// decide if we should preemptively log out before the next request.
const tokenExpiry = (token) => {
  if (!token || typeof token !== 'string') return null;
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const json = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof json.exp === 'number' ? json.exp * 1000 : null;
  } catch { return null; }
};

export const AuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('adminToken'));
  const [loading, setLoading] = useState(true);
  const fetchPatchedRef = useRef(false);
  const axiosInterceptorRef = useRef(null);

  // ---------- Hard logout (used by 401 handler + manual logout) ----------
  const handleSessionExpired = (reason = 'expired') => {
    if (sessionExpiredGuard) return; // already handling
    sessionExpiredGuard = true;
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminData');
    setToken(null);
    setAdmin(null);
    if (reason === 'expired') {
      toast.error('Session expired — please log in again', { id: 'session-expired' });
    }
    // Use replace so back-button doesn't return to broken state.
    if (!window.location.pathname.startsWith('/admin/login')) {
      // Preserve where the user was so we can return after re-login.
      try { sessionStorage.setItem('postLoginRedirect', window.location.pathname); } catch {}
      window.location.replace('/admin/login');
    }
  };

  // ---------- Rehydrate on mount ----------
  useEffect(() => {
    const storedToken = localStorage.getItem('adminToken');
    const storedAdmin = localStorage.getItem('adminData');
    if (storedToken && storedAdmin) {
      const exp = tokenExpiry(storedToken);
      if (exp && exp <= Date.now()) {
        // Token already dead — wipe quietly, don't toast on first paint.
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminData');
        setToken(null);
        setAdmin(null);
      } else {
        setToken(storedToken);
        try { setAdmin(JSON.parse(storedAdmin)); } catch { setAdmin(null); }
      }
    }
    setLoading(false);
  }, []);

  // ---------- Global axios interceptor — catches every axios.* call ----------
  useEffect(() => {
    if (axiosInterceptorRef.current !== null) return;
    axiosInterceptorRef.current = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        const detail = error?.response?.data?.detail;
        if (looksLikeAuthError(status, detail)) {
          handleSessionExpired('expired');
        }
        return Promise.reject(error);
      },
    );
    return () => {
      if (axiosInterceptorRef.current !== null) {
        axios.interceptors.response.eject(axiosInterceptorRef.current);
        axiosInterceptorRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- Global fetch wrapper — catches every plain fetch() call ----------
  useEffect(() => {
    if (fetchPatchedRef.current) return;
    const originalFetch = window.fetch.bind(window);
    window.fetch = async (...args) => {
      const res = await originalFetch(...args);
      if (res.status === 401 || res.status === 403) {
        // Peek at body without consuming the original response.
        try {
          const cloned = res.clone();
          const body = await cloned.json().catch(() => null);
          const detail = body && body.detail;
          if (looksLikeAuthError(res.status, detail)) {
            handleSessionExpired('expired');
          }
        } catch { /* non-JSON body — still treat 401 as expired */
          if (res.status === 401) handleSessionExpired('expired');
        }
      }
      return res;
    };
    window.fetch.__patched = true;
    fetchPatchedRef.current = true;
    return () => {
      // We never restore — any subsequent unmounts shouldn't unpatch.
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- Pre-emptive expiry watchdog ----------
  // If token is < 30s from expiry, log out gracefully BEFORE the next API call
  // returns 401. Avoids flash-of-empty-state UX.
  useEffect(() => {
    if (!token) return;
    const exp = tokenExpiry(token);
    if (!exp) return;
    const ms = exp - Date.now() - 30_000;
    if (ms <= 0) { handleSessionExpired('expired'); return; }
    const id = setTimeout(() => handleSessionExpired('expired'), ms);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ---------- Public API ----------
  const login = (tokenValue, adminData) => {
    sessionExpiredGuard = false;
    localStorage.setItem('adminToken', tokenValue);
    localStorage.setItem('adminData', JSON.stringify(adminData));
    setToken(tokenValue);
    setAdmin(adminData);
  };

  const logout = () => {
    sessionExpiredGuard = false;
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminData');
    setToken(null);
    setAdmin(null);
  };

  const isAuthenticated = () => !!token;

  return (
    <AuthContext.Provider value={{ admin, token, login, logout, isAuthenticated, loading }}>
      {children}
    </AuthContext.Provider>
  );
};
